/**
 * 🔐 CRYPTO HELPER UTILITIES
 * Base64 encoding/decoding and utility functions for encryption
 */

/**
 * Convert ArrayBuffer to Base64 string
 * @param {ArrayBuffer} buffer - ArrayBuffer to convert
 * @returns {string} Base64 encoded string
 */
export function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Convert Base64 string to ArrayBuffer
 * @param {string} base64 - Base64 encoded string
 * @returns {ArrayBuffer} Decoded ArrayBuffer
 */
export function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Convert string to ArrayBuffer
 * @param {string} str - String to convert
 * @returns {ArrayBuffer} ArrayBuffer representation
 */
export function stringToArrayBuffer(str) {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

/**
 * Convert ArrayBuffer to string
 * @param {ArrayBuffer} buffer - ArrayBuffer to convert
 * @returns {string} String representation
 */
export function arrayBufferToString(buffer) {
  const decoder = new TextDecoder();
  return decoder.decode(buffer);
}

/**
 * Generate random bytes
 * @param {number} length - Number of bytes to generate
 * @returns {Uint8Array} Random bytes
 */
export function generateRandomBytes(length) {
  return window.crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Check if Web Crypto API is available
 * @returns {boolean} True if available
 */
export function isWebCryptoAvailable() {
  return !!(window.crypto && window.crypto.subtle);
}

/**
 * Check if running in secure context (HTTPS)
 * @returns {boolean} True if secure context
 */
export function isSecureContext() {
  return window.isSecureContext;
}

/**
 * Generate a unique conversation key ID
 * @param {string} conversationId - Conversation ID
 * @returns {string} Unique key ID
 */
export function generateConversationKeyId(conversationId) {
  return `conversation_key_${conversationId}`;
}

